import React, { forwardRef, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { toggleCartOpen } from 'Scripts/Slices/cart';
import { IRootState } from 'Scripts/store';

import { useLazyHasCartQuery } from 'Services/cartApi';
import { handleCheckoutClickDatalayerPush } from 'Utils/dataLayerPushService';
import useOutsideClick from 'Hooks/useOutsideClick';

import { IHeaderViewModelTranslations, ILinkModel } from 'Types/Litium';
import MiniCart from 'Components/MiniCart';

import BagIcon from 'Icons/bag.svg?react';

type MiniCartButtonProps = {
    checkoutPage: ILinkModel;
    miniCartOpenText: IHeaderViewModelTranslations['minicartOpen'];
};

const PolymorphicComponent = forwardRef(
    (
        {
            tag,
            href,
            onClick,
            onMouseEnter,
            onKeyDown,
            children,
            className,
        }: {
            tag: string;
            href?: string;
            onClick?: () => void;
            onMouseEnter?: () => void;
            onKeyDown?: (event: React.KeyboardEvent<HTMLButtonElement>) => void;
            children?: React.ReactNode;
            className?: string;
        },
        ref: React.Ref<any>
    ) => {
        const commonProps = {
            className,
            onClick,
            ref,
        };

        if (tag === 'button') {
            return (
                <button
                    ref={ref as React.Ref<HTMLButtonElement>}
                    type="button"
                    onMouseEnter={onMouseEnter}
                    onKeyDown={onKeyDown}
                    {...commonProps}
                >
                    {children}
                </button>
            );
        }
        if (tag === 'a') {
            return (
                <a
                    href={href}
                    {...commonProps}
                    ref={ref as React.Ref<HTMLAnchorElement>}
                >
                    {children}
                </a>
            );
        }
        return null;
    }
);

const MiniCartButton = ({
    checkoutPage,
    miniCartOpenText,
}: MiniCartButtonProps) => {
    const [showMiniCart, setShowMiniCart] = useState(false);
    const [cartTimeOutId, setCartTimeOutId] =
        useState<ReturnType<typeof setTimeout>>(null);
    const [getCart] = useLazyHasCartQuery();

    const miniCartContainerRef = useRef();
    const dispatch = useDispatch();
    const {
        miniCartModel: { shoppingCart },
        setCartOpen,
        hasAnythingInCart,
    } = useSelector((state: IRootState) => state?.cart);

    const handleClickOutside = () => setShowMiniCart(false);
    const outsideRef = useOutsideClick(
        handleClickOutside,
        miniCartContainerRef
    );
    const handleOnHoverIcon = () => {
        if (shoppingCart?.totalQuantity > 0 || hasAnythingInCart) {
            setShowMiniCart(true);
        }
    };

    const handleOnMouseEnter = () => {
        if (cartTimeOutId) {
            clearTimeout(cartTimeOutId);
        }
    };

    const handleTrapFocusForMiniCartButton = (
        event: React.KeyboardEvent<HTMLButtonElement>
    ) => {
        if (event.key === 'Tab') {
            if (event.shiftKey) {
                setShowMiniCart(false);
            }
        }
    };

    const cartCriteria = shoppingCart?.totalQuantity > 0 || hasAnythingInCart;

    useEffect(() => {
        getCart();
    }, [shoppingCart]);

    useEffect(() => {
        if (!showMiniCart) {
            let timeOutShowMiniCart: ReturnType<typeof setTimeout>;

            if (shoppingCart?.totalQuantity < 1) {
                clearTimeout(timeOutShowMiniCart);
                setShowMiniCart(false);
            } else {
                timeOutShowMiniCart = setTimeout(() => {
                    setShowMiniCart(false);
                }, 4000);
                setCartTimeOutId(timeOutShowMiniCart);
            }

            if (setCartOpen) {
                setShowMiniCart(true);
                dispatch(toggleCartOpen(false));
            }

            return () => {
                clearTimeout(timeOutShowMiniCart);
            };
        }
    }, [setCartOpen, shoppingCart]);

    return (
        <>
            <div className="relative lg:py-4.5">
                <PolymorphicComponent
                    ref={miniCartContainerRef}
                    tag={cartCriteria ? 'button' : 'a'}
                    href={checkoutPage?.href}
                    onClick={() => {
                        setShowMiniCart(!showMiniCart);
                    }}
                    onKeyDown={event => handleTrapFocusForMiniCartButton(event)}
                    onMouseEnter={() => handleOnHoverIcon()}
                    className={
                        cartCriteria
                            ? 'relative flex'
                            : 'group/link relative flex items-center gap-2 font-montserrat text-sm font-normal text-black'
                    }
                >
                    {cartCriteria ? (
                        <span className="sr-only">{miniCartOpenText}</span>
                    ) : (
                        <span className="sr-only">{checkoutPage?.text}</span>
                    )}
                    <BagIcon className="h-6 w-6" />
                </PolymorphicComponent>
                <span
                    className={`pointer-events-none absolute right-[-3px] top-0.5 h-2.5 w-2.5 rounded-full bg-cadillac transition-[visibility,_opacity] duration-layout lg:top-5 ${
                        cartCriteria
                            ? 'visible opacity-100'
                            : 'invisible opacity-0'
                    }`}
                />
            </div>
            {showMiniCart && (
                <div
                    ref={outsideRef as React.RefObject<HTMLDivElement>}
                    className="scroll-bar-gsd::-webkit-scrollbar scroll-bar-gsd::-webkit-scrollbar-thumb scroll-bar-gsd absolute right-0 top-full z-20 max-h-[60dvh] overflow-y-auto bg-white shadow-lg min-[550px]:right-[18px] lg:right-14 lg:max-h-[488px]"
                >
                    <MiniCart
                        handleIsHover={handleOnMouseEnter}
                        isOpen={showMiniCart}
                        setIsOpen={setShowMiniCart}
                    />
                </div>
            )}
            <a
                aria-label={checkoutPage?.text}
                className="group/link hidden items-center gap-2 font-montserrat text-sm font-normal text-black lg:flex lg:py-5"
                href={checkoutPage?.href}
                onClick={() => handleCheckoutClickDatalayerPush()}
            />
        </>
    );
};

export default MiniCartButton;
